import React, { useEffect, useRef, useState } from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { Portal, Root } from '@radix-ui/react-select';
import { useInputState } from 'theming/hooks/useInputState';
import { createAssignSharedRefs } from 'utilities/input.helpers';
import { useFormContext } from 'components/Form/FormContext';
import { CaretDownIcon } from 'components/Icon/CaretDown';
import { determineComponentState } from 'components/InputBase/InputBase.utils';
import { InputErrorMessage } from 'components/InputErrorMessage/InputErrorMessage';
import { InputLabel } from 'components/InputLabel/InputLabel';
import { Tooltip, TooltipProvider } from 'components/Tooltip/Tooltip';
import { SelectItem } from './SelectItem';
import * as S from './Select.styles';
const Select = /*#__PURE__*/React.forwardRef(({
  ariaLabel,
  id,
  label,
  options,
  placeholder,
  hasError,
  hidePlaceholder,
  errorMessage,
  disabled,
  readOnly,
  size,
  width = '100%',
  value,
  open,
  onOpenChange,
  defaultOpen,
  onChange,
  required,
  optional,
  testId = id,
  valueTestId = `${id}-value`,
  clearEnabled
}, ref) => {
  const [internalValue, setInternalValue] = useState('');
  const [internalOpen, setInternalOpen] = useState(!!open);
  const wrapperRef = useRef(null);
  const internalRef = useRef(null);
  const {
    isFocused,
    isHovered
  } = useInputState(internalRef);
  const context = useFormContext();
  const isInteractive = !Boolean(disabled || readOnly);

  // We need to make every select controlled; See: https://github.com/radix-ui/primitives/issues/1658
  const handleOpenChange = v => {
    const handler = () => {
      setInternalOpen(v);
      if (onOpenChange) {
        onOpenChange(v);
      }
    };
    // Timeout isn't being cleared inside an effect for 3 main reasons:
    // - This is a temporary fix: https://github.com/radix-ui/primitives/pull/2085;
    // - The chances of this timeout not being cleared are minimal;
    // - Even in the abismal chance of not being executed within the component life cycle, isn't an expensive operation;
    setTimeout(handler, 0);
  };
  useEffect(() => {
    const isClickedOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setInternalOpen(false);
      }
    };
    window.addEventListener('click', isClickedOutside);
    return () => {
      window.removeEventListener('click', isClickedOutside);
    };
  }, []);
  const handleValueChange = v => {
    if (onChange) {
      onChange(v);
    }
    setInternalValue(v);
  };
  const handleClearAction = () => {
    if (onChange) {
      onChange('');
    }
    setInternalValue('');
  };
  const rootProps = {
    value: value ?? internalValue,
    onValueChange: handleValueChange,
    open: open ?? internalOpen,
    onOpenChange: handleOpenChange,
    defaultOpen
  };
  const placeholderOption = /*#__PURE__*/React.createElement(SelectItem, {
    size: size,
    isPlaceholder: true,
    key: placeholder,
    value: ""
  }, placeholder, "...");
  const renderOptions = () => {
    if (Array.isArray(options)) {
      const opts = options.map((option, index) => /*#__PURE__*/React.createElement(SelectOptionComponent, {
        option: option,
        key: `${id}-${index}`
      }));
      if (hidePlaceholder) {
        return opts;
      }
      return [placeholderOption, ...opts];
    }
    const groups = Object.keys(options);
    const renderedGroups = groups.map(key => {
      const group = options[key];
      return /*#__PURE__*/React.createElement(S.SelectGroup, {
        key: key
      }, /*#__PURE__*/React.createElement(S.SelectGroupLabel, null, key), group.map((group, idx) => /*#__PURE__*/React.createElement(SelectOptionComponent, {
        option: group,
        key: `${key}-${idx}`,
        grouped: true
      })));
    });
    return [placeholderOption, ...renderedGroups];
  };
  const getTitle = () => {
    if (!internalValue && !value) {
      return placeholder;
    }
    if (Array.isArray(options)) {
      const option = options.find(o => o.value === value || o.value === internalValue);
      return option?.label;
    }
    for (const optionsGroup of Object.values(options)) {
      const option = optionsGroup.find(o => o.value === value || o.value === internalValue);
      if (option) {
        return option.label;
      }
    }
  };
  const inputState = determineComponentState({
    error: hasError,
    disabled,
    readOnly,
    isFocused,
    isHovered
  });
  const assignSharedRefs = createAssignSharedRefs(ref, internalRef);
  const getSize = () => {
    if (size) {
      return size;
    }
    if (context?.inputSize) {
      return context.inputSize;
    }
    return 'large';
  };
  const hasErrorMessage = inputState === 'error' && errorMessage;
  return /*#__PURE__*/React.createElement(TooltipProvider, null, /*#__PURE__*/React.createElement(S.SelectWrapper, {
    $isInteractive: isInteractive,
    ref: wrapperRef
  }, /*#__PURE__*/React.createElement(Root, rootProps, /*#__PURE__*/React.createElement(S.HeaderContainer, null, !!label ? /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: id,
    inputState: inputState,
    optional: optional,
    required: required
  }, label) : null, clearEnabled ? /*#__PURE__*/React.createElement("div", {
    role: "button",
    onClick: handleClearAction
  }, /*#__PURE__*/React.createElement(S.ClearButton, {
    variant: "ui",
    size: "small"
  }, "Clear")) : null), /*#__PURE__*/React.createElement(S.SelectTrigger, {
    ref: assignSharedRefs,
    id: id,
    "aria-label": ariaLabel,
    disabled: disabled,
    "data-testid": testId,
    $width: width,
    $size: getSize(),
    $state: inputState,
    $isInteractive: isInteractive
  }, /*#__PURE__*/React.createElement(S.SelectValueContainer, {
    $isPlaceholder: !internalValue && !value,
    $state: inputState,
    title: getTitle()
  }, /*#__PURE__*/React.createElement(S.SelectValue, {
    $size: getSize(),
    "data-testid": valueTestId
  })), /*#__PURE__*/React.createElement(S.ChevronWrapper, {
    $state: inputState
  }, /*#__PURE__*/React.createElement(CaretDownIcon, null))), /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(S.SelectContent, {
    position: "popper",
    avoidCollisions: true
  }, /*#__PURE__*/React.createElement(ScrollArea.Root, {
    type: "scroll"
  }, /*#__PURE__*/React.createElement(S.SelectViewport, {
    asChild: true
  }, /*#__PURE__*/React.createElement(ScrollArea.Viewport, {
    role: "listbox",
    style: {
      overflowY: undefined
    }
  }, renderOptions())), /*#__PURE__*/React.createElement(S.Scrollbar, {
    orientation: "vertical"
  }, /*#__PURE__*/React.createElement(S.ScrollbarThumb, null)))))), hasErrorMessage ? /*#__PURE__*/React.createElement(InputErrorMessage, null, errorMessage) : null));
});
const SelectItemLabel = ({
  label,
  subLabel
}) => /*#__PURE__*/React.createElement(React.Fragment, null, label, subLabel ? /*#__PURE__*/React.createElement(S.SelectItemSubLabel, {
  size: "small",
  variant: "ui"
}, subLabel) : null);
const SelectOptionComponent = ({
  option,
  grouped = false
}) => {
  const {
    tooltip,
    size,
    value,
    disabled,
    label,
    subLabel
  } = option;
  const selectItemProps = {
    size,
    value,
    disabled,
    grouped
  };
  if (tooltip) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      content: tooltip
    }, /*#__PURE__*/React.createElement(SelectItem, selectItemProps, /*#__PURE__*/React.createElement(SelectItemLabel, {
      label: label,
      subLabel: subLabel
    })));
  }
  return /*#__PURE__*/React.createElement(SelectItem, selectItemProps, /*#__PURE__*/React.createElement(SelectItemLabel, {
    label: label,
    subLabel: subLabel
  }));
};
export { Select };